/**
 * campaign-export BUILDER — turns a campaign's raw leads_cards rows into the
 * platform's 50-column export, as XLSX (SheetJS, lazy-loaded) and/or CSV.
 *
 * Source: the RAW leads_cards response rows (n8nService.getLeadsCards), NOT the
 * mapped cards — MinimalLeadData deliberately drops the nested levels, and the
 * export's column contract needs them. One extra network call, only on demand.
 */
import { normalizeLeadStatus } from './status';
import { EXPORT_COLUMNS } from './columns';
import { EXPORT_STATUSES } from './columns';
import { str } from './fields';

/** Scope filter — statuses the caller wants (defaults to the full pipeline
 *  stage list). Mirrors "what the report shows": the caller passes the same
 *  admin-saved statuses the directory renders. */
const inExportScope = (lead: any, statuses: readonly string[]): boolean => statuses.includes(normalizeLeadStatus(lead?.status));

export interface ExportRowsResult { rows: Record<string, string>[]; skipped: number }

/** Map raw lead rows → ordered plain-object rows keyed by column header. */
export function buildExportRows(leads: any[], statuses: readonly string[] = EXPORT_STATUSES): ExportRowsResult {
  const scoped = (leads || []).filter(l => inExportScope(l, statuses));
  const rows = scoped.map(lead => {
    const row: Record<string, string> = {};
    for (const { col, get } of EXPORT_COLUMNS) row[col] = get(lead);
    return row;
  });
  return { rows, skipped: (leads || []).length - scoped.length };
}

/** CSV: UTF-8 BOM + CRLF, matching the platform template file. */
export function toCsv(rows: Record<string, string>[]): string {
  const header = EXPORT_COLUMNS.map(c => c.col);
  const esc = (v: string) => (/[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const lines = [header.join(','), ...rows.map(r => header.map(h => esc(r[h] ?? '')).join(','))];
  return '﻿' + lines.join('\r\n') + '\r\n';
}

/** XLSX via SheetJS — dynamic import keeps it out of the initial bundle. */
export async function toXlsx(rows: Record<string, string>[], sheetName = 'Sheet'): Promise<Blob> {
  const XLSX = await import('xlsx');
  const ws = XLSX.utils.json_to_sheet(rows, { header: EXPORT_COLUMNS.map(c => c.col) });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

export const slugify = (name: string) => name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'campaign';

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
